import { LuArrowUpRight } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { usePositions } from '@/cip/api/position';
import { TickerLogo } from '@/components/TickerLogo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency, formatPercent } from 'utils';
import { pnlColorClass } from 'views/account/pnlCellRenderers';

interface TopPositionsCardProps {
    accountGUID: string;
    positionsPath: string;
}

const formatGain = (value?: number) => {
    if (value === undefined || !Number.isFinite(value)) return '—';
    return `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatCurrency(Math.abs(value))}`;
};

export const TopPositionsCard = ({ accountGUID, positionsPath }: TopPositionsCardProps) => {
    const { data, isPending, isPlaceholderData, isError } = usePositions(
        {
            dataVariant: 'report',
            pagination: { pageIndex: 0, pageSize: 10 },
            sorting: [{ id: 'marketValue', desc: true }],
            columnFilters: [{ id: 'portfolioGuid', value: [accountGUID] }],
        },
        { throwOnError: false },
    );
    const isLoading = isPending || isPlaceholderData;
    const positions = data?.positions ?? [];
    const weight = positions.reduce((total, { base }) => total + base.marketValuePercentOfNav, 0);

    return (
        <Card className="border-border overflow-hidden rounded-2xl bg-white xl:col-span-2">
            <div className="flex flex-wrap items-center gap-4 p-4 lg:p-6">
                <h2 className="font-heading text-cocoa-1000 text-xl">Top 10 positions</h2>
                {!isLoading && !isError && data && (
                    <span className="text-cocoa-500 text-sm">
                        {Number.isFinite(weight) ? formatPercent(weight * 100, 1) : '—'} of account · {data.totalNr}{' '}
                        holdings total
                    </span>
                )}
                <Button asChild variant="outline" size="icon" className="ml-auto shrink-0 rounded-lg">
                    <Link to={positionsPath} aria-label="View all account positions">
                        <LuArrowUpRight className="h-5 w-5" />
                    </Link>
                </Button>
            </div>
            <Table className="text-sm">
                <TableHeader>
                    <TableRow className="bg-cocoa-50 hover:bg-cocoa-50">
                        <TableHead className="text-cocoa-500 px-6 text-[10px] font-medium tracking-wider uppercase">
                            Ticker
                        </TableHead>
                        <TableHead className="text-cocoa-500 text-[10px] font-medium tracking-wider uppercase">
                            Name
                        </TableHead>
                        {['Value', '% Acct', 'Unreal. gain'].map((heading) => (
                            <TableHead
                                key={heading}
                                className="text-cocoa-500 px-6 text-right text-[10px] font-medium tracking-wider whitespace-nowrap uppercase"
                            >
                                {heading}
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading || isError || positions.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={5} className="text-cocoa-400 py-8 text-center">
                                {isLoading
                                    ? 'Loading positions…'
                                    : isError
                                      ? 'Unable to load positions'
                                      : 'No positions available'}
                            </TableCell>
                        </TableRow>
                    ) : (
                        positions.map(({ base }) => (
                            <TableRow key={JSON.stringify([base.tradeDate, base.pId, base.aC, base.sId])}>
                                <TableCell className="text-cocoa-1000 px-6 py-3 font-medium">
                                    <div className="flex items-center gap-3">
                                        <span className="border-border flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border bg-white p-1">
                                            <TickerLogo
                                                tickerSymbol={base.symbol}
                                                companyName={base.description}
                                                className="h-full w-full object-contain"
                                                fallbackIconClassName="text-cocoa-400 h-4 w-4"
                                            />
                                        </span>
                                        {base.symbol || '—'}
                                    </div>
                                </TableCell>
                                <TableCell className="text-cocoa-900 py-3">{base.description}</TableCell>
                                <TableCell className="text-cocoa-1000 px-6 py-3 text-right font-medium tabular-nums">
                                    {Number.isFinite(base.marketValue) ? formatCurrency(base.marketValue) : '—'}
                                </TableCell>
                                <TableCell className="text-cocoa-700 px-6 py-3 text-right tabular-nums">
                                    {Number.isFinite(base.marketValuePercentOfNav)
                                        ? formatPercent(base.marketValuePercentOfNav * 100, 1)
                                        : '—'}
                                </TableCell>
                                <TableCell
                                    className={`px-6 py-3 text-right tabular-nums ${pnlColorClass(base.pnlUnrealized)}`}
                                >
                                    {formatGain(base.pnlUnrealized)}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </Card>
    );
};
